// @flow
import type { AbstractComponent, Element, MixedElement } from 'react';
import React from 'react';
import { Field, FieldArray } from 'redux-form';
import type { OptionsProp, State } from './support/types/options.js';
import RenderInput from './RenderInput/index.jsx';
import { getOptionElements, getSelectedOptionText } from './support/options.jsx';

type Props = {
  form: string,
  name: string,
  item: string,
  options?: OptionsProp | null,
  readOnly?: boolean,
  readOnlyComponent?: AbstractComponent<{}> | string | null,
  render?: Function,
  state: State,
  type: string,
  value: any,
  format: Function,
  normalize: Function,
  autofilled?: boolean,
};

const BaseField = ({
  form,
  name,
  item,
  options = null,
  readOnly = false,
  readOnlyComponent = null,
  render = null,
  state,
  type,
  value,
  format,
  normalize,
  // New props for autofilled support with safe defaults
  autofilled = false,
}: Props): Element<typeof Field | typeof FieldArray | AbstractComponent<{}>> | MixedElement => {
  // Disable field if autofilled (similar to current model form behavior)
  const shouldBeReadOnly = readOnly || autofilled;
  if (type === 'FieldArray' && render) {
    return (
      <FieldArray
        item={item}
        component={render}
        form={form}
        id={name}
        name={name}
        readOnly={shouldBeReadOnly}
      />
    );
  }

  if (readOnly && readOnlyComponent) {
    const displayValue =
      type === 'select' && options ? getSelectedOptionText(options, value, state) : value;

    return React.createElement(
      readOnlyComponent,
      { id: name },
      typeof format === 'function' ? format(displayValue) : displayValue
    );
  }

  if (type === 'select' && options) {
    const optionElements = getOptionElements(options, state);
    return (
      <Field
        id={name}
        item={item}
        name={name}
        type={type}
        component={RenderInput}
        disabled={shouldBeReadOnly}
        format={format}
        normalize={normalize}
      >
        {optionElements}
      </Field>
    );
  }

  return (
    <Field
      id={name}
      item={item}
      name={name}
      type={type}
      component={RenderInput}
      readOnly={readOnly}
      disabled={autofilled}
      format={format}
      normalize={normalize}
    />
  );
};

export default BaseField;
